import React, { FC, useCallback } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { Grid, Theme } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { UpdateBusinessEntityDetailsRequestDto } from '@sparling/financial-common'
import { classValidatorResolver } from '@hookform/resolvers/class-validator'
import { makeStyles } from '@mui/styles'
import { updateBusinessEntityDetailsRequest } from '../../../../store/actions'
import { getBusinessEntityDetailsData } from '../../../../store/selectors'
import { Modal } from '../../../../components/Modal'
import { InputField } from '../../../../components/InputField'
import { GreenCheckBox } from '../../../../components/CheckBox'

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  inputWrap: {
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
  },
  activeCheckbox: {
    display: 'flex',
    marginTop: 24,
  },
}))

interface UpdateBusinessEntityDetailsModalProps {
  id: string
  isOpen: boolean
  modalClose: () => void
}

export const UpdateBusinessEntityDetailsModal: FC<UpdateBusinessEntityDetailsModalProps> = ({
  id,
  isOpen,
  modalClose,
}) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const entity = useSelector(getBusinessEntityDetailsData)

  const formMethods = useForm<UpdateBusinessEntityDetailsRequestDto>({
    mode: 'all',
    reValidateMode: 'onChange',
    shouldFocusError: true,
    resolver: classValidatorResolver(UpdateBusinessEntityDetailsRequestDto),
    defaultValues: {
      name: entity?.name,
      description: entity?.description,
      active: entity?.active,
    },
  })
  const {
    handleSubmit,
    getValues,
    formState: { isValid },
  } = formMethods

  const onSubmit = useCallback(() => {
    dispatch(updateBusinessEntityDetailsRequest({ id, ...getValues() }))
    modalClose()
  }, [dispatch, id, getValues, modalClose])

  return (
    <Modal
      title={'Update Business Entity Details'}
      isOpen={isOpen}
      modalClose={modalClose}
      onSubmit={handleSubmit(onSubmit)}
      isSubmitDisabled={!isValid}
      width={600}
    >
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container className={classes.container}>
            <Grid item container className={classes.inputWrap}>
              <InputField inputName={'name'} labelName={'Name'} inputType={'text'} fullWidth />
              <InputField
                inputName={'description'}
                labelName={'Description'}
                inputType={'text'}
                multiline
                rows={3}
                withoutPadding
                fullWidth
              />
              <Grid className={classes.activeCheckbox} component={'div'}>
                <GreenCheckBox checkBoxLabel={'Active'} checkBoxName={'active'} status />
              </Grid>
            </Grid>
          </Grid>
        </form>
      </FormProvider>
    </Modal>
  )
}
